/**
 * The borrower's own sections of the 1003 — citizenship, the subject property,
 * assets, the Section 5 declarations and the authorizations.
 *
 * Filled in by the CLIENT, not the loan officer. These are attestations about
 * the borrower's own affairs, and an agent typing them on the borrower's behalf
 * produces a signed document asserting things the signer never said.
 *
 * Three decisions worth knowing about:
 *
 *  - **Declarations start UNANSWERED, not "No".** Pre-selecting the benign
 *    answer is how fifteen questions get skipped in four seconds, and an
 *    unanswered declaration is not the same as a denied one on a credit
 *    application. Save stays disabled until every one has been touched, and the
 *    count of what is left is shown rather than hidden behind a failed submit.
 *  - **The whole set saves at once.** They are answered as a set and signed as a
 *    set; a partial save would leave a record that looks complete.
 *  - **Wording is the form's, near-verbatim.** A borrower attesting to a
 *    paraphrase has not read what they signed.
 */

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { mortgageService } from "@re-platform/api-client";
import type {
  AssetKind,
  CitizenshipType,
  Declarations,
  UrlaAsset,
  UrlaSections,
} from "@re-platform/types";
import {
  Button,
  Card,
  PlacesAutocomplete,
  Toast,
  type SelectedPlace,
} from "@re-platform/ui";

import { api } from "../../lib/api";

const CITIZENSHIP: { value: CitizenshipType; label: string }[] = [
  { value: "US_CITIZEN", label: "U.S. citizen" },
  { value: "PERMANENT_RESIDENT", label: "Permanent resident alien" },
  { value: "NON_PERMANENT_RESIDENT", label: "Non-permanent resident alien" },
];

const ASSET_KINDS: { value: AssetKind; label: string }[] = [
  { value: "CHECKING", label: "Checking account" },
  { value: "SAVINGS", label: "Savings account" },
  { value: "RETIREMENT", label: "Retirement account" },
  { value: "INVESTMENT", label: "Investments" },
  { value: "REAL_ESTATE", label: "Real estate" },
  { value: "GIFT", label: "Gift funds" },
  { value: "OTHER", label: "Other" },
];

/** Order matters — it is the order on the form the borrower signs. */
const DECLARATION_QUESTIONS: { key: keyof Declarations; label: string }[] = [
  {
    key: "will_occupy_as_primary_residence",
    label: "Will you occupy the property as your primary residence?",
  },
  {
    key: "ownership_interest_last_3_years",
    label:
      "Have you had an ownership interest in another property in the last three years?",
  },
  {
    key: "borrowing_undisclosed_money_for_this_transaction",
    label:
      "Are you borrowing any money for this real estate transaction (e.g. money for your closing costs or down payment) or obtaining any money from another party, such as the seller or realtor, that you have not disclosed on this loan application?",
  },
  {
    key: "applying_for_other_mortgage_before_closing",
    label:
      "Have you or will you be applying for a mortgage loan on another property (not the property securing this loan) on or before closing this transaction that is not disclosed on this loan application?",
  },
  {
    key: "applying_for_new_credit_before_closing",
    label:
      "Have you or will you be applying for any new credit (e.g. installment loan, credit card, etc.) on or before closing this loan that is not disclosed on this application?",
  },
  {
    key: "outstanding_judgments",
    label: "Are there any outstanding judgments against you?",
  },
  {
    key: "bankrupt_past_7_years",
    label: "Have you been declared bankrupt within the past 7 years?",
  },
  {
    key: "preforeclosure_or_short_sale_past_7_years",
    label:
      "Within the past 7 years, have you completed a pre-foreclosure sale or short sale, whereby the property was sold to a third party and the lender agreed to accept less than the outstanding mortgage balance due?",
  },
  {
    key: "foreclosure_or_deed_in_lieu_past_7_years",
    label:
      "Have you had property foreclosed upon or given title or deed in lieu thereof in the last 7 years?",
  },
  { key: "party_to_a_lawsuit", label: "Are you a party to a lawsuit?" },
  {
    key: "obligated_on_a_loan_that_ended_in_foreclosure_or_judgment",
    label:
      "Have you directly or indirectly been obligated on any loan which resulted in foreclosure, transfer of title in lieu of foreclosure, or judgment? (This includes home mortgage loans, SBA loans, home improvement loans, educational loans and manufactured (mobile) home loans.)",
  },
  {
    key: "delinquent_or_in_default_on_federal_debt",
    label:
      "Are you presently delinquent or in default on any Federal debt or any other loan, mortgage, financial obligation, bond, or loan guarantee?",
  },
  {
    key: "obligated_to_pay_alimony_child_support_or_maintenance",
    label:
      "Are you obligated to pay alimony, child support, or separate maintenance?",
  },
  {
    key: "down_payment_partly_borrowed",
    label: "Is any part of the down payment borrowed?",
  },
  {
    key: "co_maker_or_endorser_on_a_note",
    label: "Are you a co-maker or endorser on a note?",
  },
];

const ACKNOWLEDGEMENTS = [
  {
    key: "information_verification" as const,
    label:
      "I grant permission to obtain all information deemed necessary to process this mortgage loan application. This includes, but is not limited to, my past and present employment status, deposit accounts, consumer credit record, mortgage record and rental record.",
  },
  {
    key: "identity_verification_notice" as const,
    label:
      "To help the government fight the funding of terrorism and money laundering, Federal law requires all financial institutions to obtain, verify and record information identifying each person who opens an account. I understand I will be asked for my name, address, date of birth and other identifying information, and may be asked for a driver's licence or other identifying documents.",
  },
  {
    key: "electronic_disclosures" as const,
    label:
      "By applying online I agree to accept all applicable disclosures associated with this transaction in electronic form, including those required by the Truth in Lending Act, the Real Estate Settlement Procedures Act and the Equal Credit Opportunity Act. Paper copies will be provided for certain real estate related transactions after the application is received.",
  },
];

/** Unanswered is a real state, distinct from No — hence `undefined`, not false. */
type DeclarationDraft = Partial<Record<keyof Declarations, boolean>>;

export function LoanApplication() {
  const { applicationId: routeId } = useParams();
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);

  const [citizenship, setCitizenship] = useState<CitizenshipType | "">("");
  const [identified, setIdentified] = useState(false);
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [price, setPrice] = useState("");
  const [assets, setAssets] = useState<UrlaAsset[]>([]);
  const [decl, setDecl] = useState<DeclarationDraft>({});
  const [acks, setAcks] = useState({
    information_verification: false,
    identity_verification_notice: false,
    electronic_disclosures: false,
  });
  const [creditConsent, setCreditConsent] = useState<boolean | undefined>(
    undefined,
  );

  // Reachable two ways: a loan officer opens a specific application, and the
  // borrower arrives from their checklist with no id at all. Onboarding creates
  // no application row, so the id-less path asks the server for theirs (which
  // opens one if this is their first visit) rather than dead-ending.
  const mine = useQuery({
    queryKey: ["my-application"],
    queryFn: () => mortgageService.myApplication(api),
    enabled: !routeId,
  });
  const applicationId = routeId ?? mine.data?.id ?? "";

  const queryKey = ["urla", applicationId];
  const q = useQuery({
    queryKey,
    queryFn: () => mortgageService.getUrla(api, applicationId),
    enabled: Boolean(applicationId),
  });

  // Seed from a previous save so the borrower revises rather than retypes.
  useEffect(() => {
    const saved = q.data;
    if (!saved) return;
    setCitizenship(saved.citizenship);
    setIdentified(saved.subject_property.identified);
    setStreet(saved.subject_property.address_street ?? "");
    setCity(saved.subject_property.address_city ?? "");
    setState(saved.subject_property.address_state ?? "");
    setZip(saved.subject_property.address_zip ?? "");
    setPrice(saved.subject_property.purchase_price ?? "");
    setAssets(saved.assets);
    setDecl(saved.declarations);
    setAcks({
      information_verification: saved.authorizations.information_verification,
      identity_verification_notice:
        saved.authorizations.identity_verification_notice,
      electronic_disclosures: saved.authorizations.electronic_disclosures,
    });
    setCreditConsent(saved.authorizations.credit_check_consent);
  }, [q.data]);

  const save = useMutation({
    mutationFn: (body: UrlaSections) =>
      mortgageService.putUrla(api, applicationId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      setToast("Saved. Your loan officer can see your application now.");
    },
    onError: (err: Error) => setToast(err.message),
  });

  const unanswered = useMemo(
    () => DECLARATION_QUESTIONS.filter((q) => decl[q.key] === undefined).length,
    [decl],
  );
  const allAcked = Object.values(acks).every(Boolean);
  const ready =
    citizenship !== "" &&
    unanswered === 0 &&
    allAcked &&
    creditConsent !== undefined &&
    (!identified || street.trim() !== "");

  function submit() {
    // `ready` carries an inferred type predicate, so this narrows `citizenship`
    // to a real value — no second, redundant check needed.
    if (!ready) return;
    save.mutate({
      citizenship,
      subject_property: {
        identified,
        address_street: street.trim() || null,
        address_city: city.trim() || null,
        address_state: state.trim() || null,
        address_zip: zip.trim() || null,
        purchase_price: price.trim() || null,
      },
      assets,
      declarations: decl as Declarations,
      authorizations: {
        ...acks,
        credit_check_consent: creditConsent === true,
      },
    });
  }

  if (!applicationId) {
    return (
      <p style={hint}>
        {mine.isError
          ? `Couldn't open your loan application: ${(mine.error as Error).message}`
          : "Opening your loan application…"}
      </p>
    );
  }

  return (
    <>
      <h1 style={{ marginTop: 0 }}>Your loan application</h1>
      <p style={{ color: "var(--rp-color-text-muted)", marginTop: 0 }}>
        These are the parts of the application only you can answer. Everything
        here goes on the form you sign, so take your time.
      </p>

      <Card padding="md" style={{ marginTop: 16 }}>
        <h2 style={h2}>Citizenship</h2>
        {CITIZENSHIP.map((c) => (
          <label key={c.value} style={radioRow}>
            <input
              type="radio"
              name="citizenship"
              checked={citizenship === c.value}
              onChange={() => setCitizenship(c.value)}
            />
            <span>{c.label}</span>
          </label>
        ))}
      </Card>

      <Card padding="md" style={{ marginTop: 16 }}>
        <h2 style={h2}>The property</h2>
        <label style={radioRow}>
          <input
            type="checkbox"
            checked={identified}
            onChange={(e) => setIdentified(e.target.checked)}
          />
          <span>I have found the property I want to buy</span>
        </label>
        {!identified && (
          <p style={hint}>
            That's fine — most people get pre-approved first. You can add it
            later.
          </p>
        )}
        {identified && (
          <div
            style={{ display: "grid", gap: 10, marginTop: 10, maxWidth: 460 }}
          >
            {/* The SUBJECT property of the loan — it lands on the 1003 and is
                what the appraisal and the title work are ordered against, so a
                mistyped street is expensive rather than untidy. Picking a
                suggestion fills the three boxes below; they stay editable
                because with no API key this degrades to a plain input. */}
            <label style={fieldLabel}>
              <span style={labelText}>Street address</span>
              <PlacesAutocomplete
                apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                className=""
                style={input}
                value={street}
                onChange={setStreet}
                onSelect={(p: SelectedPlace) => {
                  setStreet(p.street || p.address);
                  if (p.city) setCity(p.city);
                  if (p.state) setState(p.state);
                  if (p.zip) setZip(p.zip);
                }}
                placeholder="Street address"
              />
            </label>
            <div style={{ display: "flex", gap: 10 }}>
              <Field label="City" value={city} onChange={setCity} />
              <Field
                label="State"
                value={state}
                onChange={setState}
                width={70}
              />
              <Field label="ZIP" value={zip} onChange={setZip} width={100} />
            </div>
            <Field
              label="Purchase price (or your best estimate)"
              value={price}
              onChange={setPrice}
              width={160}
            />
          </div>
        )}
      </Card>

      <Card padding="md" style={{ marginTop: 16 }}>
        <h2 style={h2}>Assets</h2>
        <p style={hint}>
          Anything you could put towards the purchase — accounts, investments,
          property, gift funds. More is better: it strengthens your application.
          Your loan officer will ask for statements to verify these later.
        </p>
        {assets.map((a, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-end",
              marginTop: 10,
            }}
          >
            <label style={{ ...fieldLabel, width: 150 }}>
              <span style={labelText}>Type</span>
              <select
                style={input}
                value={a.kind}
                onChange={(e) =>
                  setAssets(
                    patch(assets, i, { kind: e.target.value as AssetKind }),
                  )
                }
              >
                {ASSET_KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
            </label>
            <Field
              label="Description"
              value={a.description}
              onChange={(v) => setAssets(patch(assets, i, { description: v }))}
              width={220}
            />
            <Field
              label="Estimated value"
              value={a.value}
              onChange={(v) => setAssets(patch(assets, i, { value: v }))}
              width={130}
            />
            <label style={{ ...radioRow, paddingBottom: 8 }}>
              <input
                type="checkbox"
                checked={a.toward_closing}
                onChange={(e) =>
                  setAssets(
                    patch(assets, i, { toward_closing: e.target.checked }),
                  )
                }
              />
              <span style={{ fontSize: 12 }}>Using at closing</span>
            </label>
            <button
              type="button"
              className="rp-btn rp-btn--sm"
              onClick={() => setAssets(assets.filter((_, j) => j !== i))}
              style={{ marginBottom: 6 }}
            >
              Remove
            </button>
          </div>
        ))}
        <div style={{ marginTop: 12 }}>
          <Button
            onClick={() =>
              setAssets([
                ...assets,
                {
                  kind: "CHECKING",
                  description: "",
                  value: "",
                  toward_closing: false,
                },
              ])
            }
            disabled={assets.length >= 40}
          >
            Add an asset
          </Button>
        </div>
      </Card>

      <Card padding="md" style={{ marginTop: 16 }}>
        <h2 style={h2}>Declarations</h2>
        <p style={hint}>
          Every one needs an answer. Nothing here is pre-filled on purpose — a
          wrong answer you didn't notice is worse than a question you had to
          read.
        </p>
        {DECLARATION_QUESTIONS.map((question) => (
          <div key={question.key} style={declRow}>
            <span style={{ fontSize: 13, flex: 1 }}>{question.label}</span>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              {[true, false].map((value) => (
                <label key={String(value)} style={yesNo}>
                  <input
                    type="radio"
                    name={question.key}
                    checked={decl[question.key] === value}
                    onChange={() => setDecl({ ...decl, [question.key]: value })}
                  />
                  <span>{value ? "Yes" : "No"}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </Card>

      <Card padding="md" style={{ marginTop: 16 }}>
        <h2 style={h2}>Authorization</h2>
        {ACKNOWLEDGEMENTS.map((a) => (
          <label key={a.key} style={{ ...radioRow, alignItems: "flex-start" }}>
            <input
              type="checkbox"
              checked={acks[a.key]}
              onChange={(e) => setAcks({ ...acks, [a.key]: e.target.checked })}
              style={{ marginTop: 4 }}
            />
            <span style={{ fontSize: 13 }}>{a.label}</span>
          </label>
        ))}
        <div style={{ ...declRow, marginTop: 6 }}>
          <span style={{ fontSize: 13, flex: 1 }}>
            I authorise a check of my credit for this application.
          </span>
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            {[true, false].map((value) => (
              <label key={String(value)} style={yesNo}>
                <input
                  type="radio"
                  name="credit_check_consent"
                  checked={creditConsent === value}
                  onChange={() => setCreditConsent(value)}
                />
                <span>{value ? "Yes" : "No"}</span>
              </label>
            ))}
          </div>
        </div>
        {creditConsent === false && (
          <p style={{ ...hint, color: "var(--rp-color-warning)" }}>
            Your loan officer can't check your rate without this, so they'll get
            in touch to talk it through.
          </p>
        )}
      </Card>

      <div
        style={{
          marginTop: 16,
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <Button onClick={submit} disabled={!ready || save.isPending}>
          {save.isPending ? "Saving…" : "Save my application"}
        </Button>
        {!ready && (
          <span style={{ ...hint, margin: 0 }}>
            {unanswered > 0
              ? `${unanswered} declaration${unanswered === 1 ? "" : "s"} left to answer`
              : citizenship === ""
                ? "Choose your citizenship status"
                : !allAcked
                  ? "All three authorisations are required"
                  : creditConsent === undefined
                    ? "Answer the credit check question"
                    : "Add the property's street address"}
          </span>
        )}
      </div>
      <Toast message={toast} onDismiss={() => setToast(null)} />
    </>
  );
}

function patch(
  assets: UrlaAsset[],
  index: number,
  changes: Partial<UrlaAsset>,
) {
  return assets.map((a, i) => (i === index ? { ...a, ...changes } : a));
}

function Field({
  label,
  value,
  onChange,
  width,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  width?: number;
}) {
  return (
    <label style={{ ...fieldLabel, width }}>
      <span style={labelText}>{label}</span>
      <input
        style={input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

const h2: React.CSSProperties = { fontSize: 15, marginTop: 0, marginBottom: 8 };

const hint: React.CSSProperties = {
  color: "var(--rp-color-text-muted)",
  fontSize: 13,
  marginTop: 6,
};

const radioRow: React.CSSProperties = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  padding: "5px 0",
  cursor: "pointer",
};

const declRow: React.CSSProperties = {
  display: "flex",
  gap: 16,
  alignItems: "flex-start",
  padding: "9px 0",
  borderTop: "1px solid var(--rp-color-border)",
};

const yesNo: React.CSSProperties = {
  display: "flex",
  gap: 4,
  alignItems: "center",
  fontSize: 13,
  cursor: "pointer",
};

const fieldLabel: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const labelText: React.CSSProperties = {
  color: "var(--rp-color-text-muted)",
  fontSize: 12,
};

const input: React.CSSProperties = {
  padding: "6px 8px",
  font: "inherit",
  fontSize: 13,
  border: "1px solid var(--rp-color-border)",
  borderRadius: "var(--rp-radius-sm)",
  background: "var(--rp-color-surface)",
  color: "var(--rp-color-text)",
  width: "100%",
};
